import type { Option, SurveyStep } from "./types";

const options = (...labels: string[]): Option[] =>
  labels.map((label, index) => ({ id: `o${index}`, label }));

export const PRODUCT = "warranty-claim-copilot" as const;
export const SURVEY_VERSION = "warranty-2026-08-11-v4";
export const GIFT_AMOUNT = "$10";
export const OTHER_TEXT_SUFFIX = "__other";

export const WARRANTY_STAGES = [
  "Finding requirements",
  "Confirming eligibility",
  "Capturing model and serial numbers",
  "Recording diagnosis",
  "Collecting photos",
  "Documenting parts and labor",
  "Obtaining technician notes",
  "Finding missing information",
  "Preparing claim forms",
  "Entering portals",
  "Responding to follow-up requests",
  "Tracking status",
  "Reconciling reimbursement",
];

export const warrantySurvey: SurveyStep[] = [
  {
    id: "landing",
    type: "landing",
    section: "Welcome",
    headline: "What if every warranty claim arrived ready to submit?",
    body: "Show us how claims move through your team today, then see a short preview of OEMReady—a guided workflow designed to catch missing details early and turn field evidence into a complete claim packet.",
  },
  {
    id: "role",
    type: "single",
    section: "Your work",
    required: true,
    title: "Which best describes your role?",
    options: options(
      "Service technician",
      "Service manager / dispatcher",
      "Office / claims admin",
      "Owner / GM",
      "Other",
      "I’m not involved in warranty claims",
    ),
    exitOptionId: "o5",
  },
  {
    id: "volume",
    type: "single",
    section: "Your work",
    required: true,
    title: "About how many manufacturer warranty claims does your team file each month?",
    options: options("1–5", "6–15", "16–30", "31–60", "60+"),
  },
  {
    id: "owner",
    type: "single",
    section: "Your workflow",
    required: true,
    title: "Who owns a claim from diagnosis through reimbursement?",
    options: options(
      "Same person, start to finish",
      "Handed off from field tech to office",
      "Handed off between multiple office staff",
      "Varies by claim",
      "Other",
    ),
  },
  {
    id: "systems",
    type: "multi",
    section: "Your workflow",
    required: true,
    title: "How does your team manage warranty claims today?",
    subtitle: "Select all that apply.",
    options: options(
      "Field-service software",
      "Manufacturer claim portals",
      "Spreadsheets",
      "Email",
      "Paper forms",
      "Phone calls",
      "Other",
    ),
  },
  {
    id: "problems",
    type: "multi",
    section: "Where it slows down",
    required: true,
    maxSelect: 3,
    title: "Where do warranty claims slow your team down most?",
    subtitle: "Choose up to three.",
    options: [
      ...options(...WARRANTY_STAGES),
      { id: "none", label: "None of these" },
      { id: "other", label: "Other" },
    ],
  },
  {
    id: "incidentfreq",
    type: "single",
    section: "Recent claims",
    required: true,
    title: "Over the past three months, how many claims were delayed, returned, reduced, or rejected?",
    options: options("0", "1–2", "3–5", "6+"),
  },
  {
    id: "incident",
    type: "short",
    section: "Recent claims",
    skippable: true,
    title: "Think of the most recent problem claim. What happened?",
    subtitle: "Where did the process break down?",
    placeholder: "For example: The portal rejected the claim for a missing serial-number photo…",
  },
  {
    id: "impact",
    type: "multi",
    section: "What it costs",
    required: true,
    title: "What did that problem create for your team?",
    subtitle: "Select all that apply.",
    options: options(
      "Office follow-up",
      "Technician follow-up",
      "Customer contact",
      "Return visit",
      "Delayed submission",
      "Delayed payment",
      "Reduced reimbursement",
      "Rejected claim",
      "Write-off",
      "Manager had to step in",
      "None of these",
    ),
  },
  {
    id: "officetime",
    type: "single",
    section: "What it costs",
    skippable: true,
    title: "How much office time does a typical claim require from start to finish?",
    options: options("Under 30 minutes", "30–60 minutes", "1–2 hours", "Over 2 hours"),
  },
  {
    id: "impactcost",
    type: "single",
    section: "What it costs",
    skippable: true,
    title: "What is the typical reimbursement impact of a delayed or rejected claim?",
    options: options("None", "Under $100", "$100–$500", "$500–$2,000", "$2,000+"),
  },
  {
    id: "demo",
    type: "demo",
    section: "See the concept",
    title: "See how OEMReady could simplify the claim",
  },
  {
    id: "reaction_useful",
    type: "single",
    section: "Your take",
    required: true,
    title: "How valuable would this workflow be for your operation?",
    options: options("Not useful", "Somewhat useful", "Useful", "Very useful", "Extremely useful"),
  },
  {
    id: "reaction_capability",
    type: "single",
    section: "Your take",
    required: true,
    title: "Which part of OEMReady would help your team most?",
    options: options(
      "Manufacturer requirement library",
      "Technician capture guidance",
      "Missing-information detection",
      "Evidence organization",
      "Draft claim preparation",
      "Human review workflow",
      "None of these",
    ),
  },
  {
    id: "reaction_change",
    type: "short",
    section: "Your take",
    skippable: true,
    title: "What would OEMReady need to fit your workflow?",
    placeholder: "Optional",
  },
  {
    id: "reaction_concerns",
    type: "multi",
    section: "Your take",
    skippable: true,
    title: "What might stop your team from using OEMReady?",
    options: options(
      "Cost",
      "Learning curve / training",
      "Reliability or accuracy",
      "Integrating with existing systems",
      "Getting the team to adopt it",
      "Data privacy / security",
      "Other",
      "No real concerns",
    ),
  },
  {
    id: "nextstep",
    type: "nextstep",
    section: "Stay involved",
    title: "Want to take a closer look at OEMReady?",
    subtitle: "Choose anything that interests you—or simply continue.",
    options: options(
      "Share a recent example with us",
      "Join a 20-minute follow-up conversation",
      "Test an early prototype",
      "Discuss a pilot",
      "Receive occasional updates",
      "No follow-up",
    ),
  },
  {
    id: "contact",
    type: "contact",
    section: "Optional contact",
    title: "Where should we follow up?",
  },
  {
    id: "giftcard",
    type: "giftcard",
    section: "Thank-you gift",
    title: "Your $10 thank-you gift card",
  },
  {
    id: "complete",
    type: "completion",
    section: "Done",
  },
];

export const questionSteps = warrantySurvey.filter((step) =>
  ["single", "multi", "ranked", "short", "nextstep"].includes(step.type),
);

export const openTextQuestionIds = new Set(
  [
    ...warrantySurvey.filter((step) => step.type === "short").map((step) => step.id),
    ...warrantySurvey
      .filter((step) => step.options?.some((option) => option.label.toLowerCase() === "other"))
      .map((step) => `${step.id}${OTHER_TEXT_SUFFIX}`),
  ],
);

export function otherTextQuestionId(stepId: string): string {
  return `${stepId}${OTHER_TEXT_SUFFIX}`;
}

export function visibleSurveySteps(answers: Record<string, string | string[]>): SurveyStep[] {
  const role = warrantySurvey.find((step) => step.id === "role");
  if (role?.exitOptionId && answers.role === role.exitOptionId) {
    return [warrantySurvey[0], role, {
      id: "screened_out",
      type: "completion",
      section: "Thanks",
      title: "Thanks for your time",
      body: "This preview is intended for people directly involved in warranty claims, so we’ll stop here. We appreciate you taking a look.",
    }];
  }

  return warrantySurvey;
}

export function optionsForStep(step: SurveyStep, answers: Record<string, string | string[]>): Option[] {
  if (step.type !== "ranked") return step.options ?? [];
  const source = warrantySurvey.find((candidate) => candidate.id === step.sourceStepId);
  const selected = Array.isArray(answers[step.sourceStepId ?? ""]) ? answers[step.sourceStepId ?? ""] : [];
  return (source?.options ?? []).filter((option) => selected.includes(option.id) && option.id !== "none" && option.id !== "other");
}

export function isPreDemoQuestion(stepId: string): boolean {
  const demoIndex = warrantySurvey.findIndex((step) => step.id === "demo");
  const stepIndex = warrantySurvey.findIndex((step) => step.id === stepId);
  return stepIndex > 0 && stepIndex < demoIndex;
}
